/**
 * SeoPerformanceTile — joins per-article pageview data with schema-audit
 * cleanliness to surface:
 *   • Aggregate lift: how much more traffic schema-clean articles get vs
 *     articles with warnings
 *   • Hidden winners: schema-clean articles in the bottom 25% of 7d views
 *     (i.e. nothing wrong with the schema — suggests OTHER SEO issues like
 *     thin content, weak title tag, low backlinks)
 *   • Per-article table sorted by 30d views
 *
 * Cost-safe: single GET to /admin/seo/performance. No LLM calls. Pageviews
 * are tracked server-side on every public article GET.
 */
import { useEffect, useState } from "react";
import {
    TrendingUp,
    AlertTriangle,
    Sparkles,
    ShieldCheck,
    RefreshCw,
    Loader2,
    Eye,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SeoPerformanceTile() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/admin/seo/performance`, {
                withCredentials: true,
            });
            setData(r.data);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not load SEO performance.",
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    if (loading && !data) {
        return (
            <div
                className="bl-card p-5 flex items-center gap-3 text-sm text-bl-muted"
                data-testid="seo-perf-loading"
            >
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading SEO performance…
            </div>
        );
    }
    if (!data) return null;

    const agg = data.aggregates || {};
    const hidden = data.flagged_clean_low_traffic || [];
    const lift = agg.lift_pct;
    const liftPositive = lift !== null && lift !== undefined && lift > 0;
    const noDirty = (agg.dirty_count || 0) === 0;

    return (
        <div className="bl-card p-5 space-y-4" data-testid="seo-perf-tile">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-bl-primary" />
                    <h3 className="font-display font-semibold text-base text-bl tracking-tight">
                        SEO performance · schema × pageviews
                    </h3>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="bl-btn-ghost text-xs gap-1"
                    data-testid="seo-perf-refresh"
                >
                    <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bl-card p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                        Views · 7d
                    </div>
                    <div className="font-display font-semibold text-2xl text-bl mt-1 font-mono-bl" data-testid="seo-perf-total-7d">
                        {agg.total_views_7d ?? 0}
                    </div>
                </div>
                <div className="bl-card p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                        Views · 30d
                    </div>
                    <div className="font-display font-semibold text-2xl text-bl mt-1 font-mono-bl" data-testid="seo-perf-total-30d">
                        {agg.total_views_30d ?? 0}
                    </div>
                </div>
                <div className="bl-card p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                        Schema clean
                    </div>
                    <div className="font-display font-semibold text-2xl text-bl mt-1 font-mono-bl text-[var(--bl-green)]">
                        {agg.clean_count ?? 0}
                    </div>
                </div>
                <div className="bl-card p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                        With warnings
                    </div>
                    <div
                        className={
                            "font-display font-semibold text-2xl mt-1 font-mono-bl " +
                            ((agg.dirty_count || 0) > 0
                                ? "text-[var(--bl-rust)]"
                                : "text-bl-muted")
                        }
                    >
                        {agg.dirty_count ?? 0}
                    </div>
                </div>
            </div>

            {/* Insight banner */}
            <div
                className="rounded border border-bl/40 px-3 py-2.5 text-xs leading-relaxed bg-bl-bg/30"
                data-testid="seo-perf-insight"
            >
                {noDirty ? (
                    <>
                        <ShieldCheck className="h-3 w-3 inline mr-1 text-[var(--bl-green)] -mt-0.5" />
                        <span className="text-bl">
                            All {agg.clean_count} articles are schema-clean — no
                            warning-vs-clean lift comparison available yet.
                            Aggregate lift will compute the moment any article
                            regresses.
                        </span>
                    </>
                ) : liftPositive ? (
                    <>
                        <Sparkles className="h-3 w-3 inline mr-1 text-bl-primary -mt-0.5" />
                        <span className="text-bl">
                            Schema-clean articles earn{" "}
                            <span className="font-medium font-mono-bl">{lift}%</span>{" "}
                            more 30d pageviews on average than articles with
                            warnings (median {agg.median_views_30d_clean} vs{" "}
                            {agg.median_views_30d_dirty}).
                        </span>
                    </>
                ) : (
                    <span className="text-bl-muted">
                        Median 30d views: clean {agg.median_views_30d_clean} ·
                        with-warnings {agg.median_views_30d_dirty}. Lift not
                        positive — either traffic is too low to draw conclusions,
                        or schema cleanliness isn't the dominant factor in your
                        catalog right now.
                    </span>
                )}
            </div>

            {/* Hidden winners — schema-clean but traffic-flat */}
            {hidden.length > 0 && (
                <div
                    className="rounded border border-bl/40"
                    data-testid="seo-perf-hidden-winners"
                >
                    <div className="px-3 py-2 border-b border-bl/30 text-[10px] uppercase tracking-[0.18em] text-bl-muted flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3 text-[var(--bl-amber)]" />
                        Hidden winners · schema-clean but traffic-flat
                    </div>
                    {hidden.map((a) => (
                        <div
                            key={a.slug}
                            className="px-3 py-2 border-b border-bl/30 last:border-0 flex items-center gap-3 text-xs"
                        >
                            <div className="text-bl flex-1 truncate" title={a.title}>
                                {a.title}
                            </div>
                            <span className="text-bl-muted text-[11px]">
                                {a.category}
                            </span>
                            <span className="text-bl-muted text-[11px] w-12 text-right font-mono-bl">
                                {a.views_7d}v / 7d
                            </span>
                        </div>
                    ))}
                    <div className="px-3 py-2 text-[10px] text-bl-muted italic leading-relaxed border-t border-bl/30">
                        Bottom-quartile clean articles. Schema is fine — investigate
                        title tag, opening paragraph, internal linking, and
                        backlink profile.
                    </div>
                </div>
            )}

            {/* Per-article table */}
            <div className="rounded border border-bl/40" data-testid="seo-perf-table">
                <div className="px-3 py-2 border-b border-bl/30 text-[10px] uppercase tracking-[0.18em] text-bl-muted flex items-center gap-3">
                    <Eye className="h-3 w-3" />
                    <span className="flex-1">Article · top by 30d views</span>
                    <span className="w-12 text-right">7d</span>
                    <span className="w-12 text-right">30d</span>
                    <span className="w-14 text-right">All-time</span>
                </div>
                <div className="max-h-80 overflow-y-auto">
                    {data.per_article.map((a) => (
                        <div
                            key={a.slug}
                            className="px-3 py-2 border-b border-bl/30 last:border-0 flex items-center gap-3 text-xs"
                        >
                            {a.has_warnings ? (
                                <AlertTriangle className="h-3 w-3 text-[var(--bl-rust)] flex-shrink-0" />
                            ) : (
                                <ShieldCheck className="h-3 w-3 text-[var(--bl-green)] flex-shrink-0" />
                            )}
                            <div className="text-bl flex-1 truncate" title={a.title}>
                                {a.title}
                            </div>
                            <span className="text-bl-muted w-12 text-right font-mono-bl">
                                {a.views_7d}
                            </span>
                            <span className="text-bl-muted w-12 text-right font-mono-bl">
                                {a.views_30d}
                            </span>
                            <span className="text-bl-muted w-14 text-right font-mono-bl">
                                {a.views_total}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <p className="text-[10px] text-bl-muted italic leading-relaxed">
                {data.tracking_note}
            </p>
        </div>
    );
}
